import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BulkRefundResponse,
  Buyer,
  Cart,
  DataApiResponseLegacy,
  DeliveryAddress,
  ExternalGiftCardBalanceModel,
  Order,
  OrderRequest,
  OrderRestaurantReservation,
  PaymentMethod,
  Refund,
  RefundMode,
  ResendOrderConfirmationResult,
  Reservation,
  ReservationRequest,
  RestaurantReservationRetryRequest,
  ShopAvailabilityRequest,
  Voucher,
} from '@shared/definitions';
import { UUID } from 'angular2-uuid';

@Injectable({
  providedIn: 'root',
})
export class OrderApiService {
  constructor(private http: HttpClient) {}

  // Session

  createSession = () => this.http.post('/api/v1.0/sessions', { frontend_session_id: UUID.UUID() });

  // Cart

  getCarts = (sessionId: string) => this.http.get<Cart[]>(`/api/v1.0/me/carts?session_id=${sessionId}`);

  getShopAvailability = (availabilityRequest: ShopAvailabilityRequest) =>
    this.http.post(`/api/v1.0/shops/availability`, availabilityRequest);

  // Reservations

  createReservation = (shopId: string, reservation: ReservationRequest) =>
    this.http.post<Reservation>(`/api/v1.0/shops/${shopId}/reservations`, reservation);

  deleteReservation = (reservationId: string) =>
    this.http.delete<{ cart_line_id: string; cart: Cart }>(`/api/v1.0/reservations/${reservationId}`);

  retryRestaurantReservation = (id: string, body: RestaurantReservationRetryRequest = {}) =>
    this.http.post<OrderRestaurantReservation>(`/api/v2.0/restaurant-reservations/${id}/retry`, body);

  // Payment methods

  getPaymentMethods = (cartId: string) => this.http.get<PaymentMethod[]>(`/api/v1.0/carts/${cartId}/payment-methods`);

  // Delivery

  setDeliveryMethod = (cartId: string, method: number) =>
    this.http.put(`/api/v1.0/carts/${cartId}/delivery-method?method=${method}`, {});

  setBuyer = (cartId: string, buyer: Buyer) => this.http.put(`/api/v1.0/carts/${cartId}/buyer`, buyer);

  setDelivery = (cartId: string, address: DeliveryAddress) =>
    this.http.put(`/api/v1.0/carts/${cartId}/delivery`, address);

  setPayAfter = (cartId: string, value: boolean) =>
    this.http.put(`/api/v1.0/carts/${cartId}/pay-after?payafter=${value}`, {});

  setTimeslot = (cartId: string, timeslot: string) =>
    this.http.put(`/api/v1.0/carts/${cartId}/time-slot?timeSlot=${encodeURIComponent(timeslot)}`, {});

  // Order

  createOrder = (orderRequest: OrderRequest, params?: Record<string, string>) =>
    this.http.post<Order>(`/api/v1.0/orders`, orderRequest, params ? { params } : {});

  confirmOrder = (orderId: string) => this.http.post<Order>(`/api/v1.0/orders/${orderId}/confirm`, {});

  addOrderDiscount = (orderId: string, code: string, context?: HttpContext) =>
    this.http.put<Order>(
      `/api/v1.0/orders/${orderId}/discount`,
      { code },
      { params: { include: 'hold_blockers' }, ...(context ? { context } : {}) }
    );

  removeOrderDiscount = (orderId: string, discountCodeId: string) =>
    this.http.delete<Order>(`/api/v1.0/orders/${orderId}/discount/${discountCodeId}`);

  addOrderPayment = (
    orderId: string,
    paymentMethodExternalId: string,
    paymentMethodCode?: string,
    paymentMethodPin?: string,
    amount?: number
  ) =>
    this.http.post<Order>(`/api/v1.0/orders/${orderId}/payments`, {
      payment_method_external_id: paymentMethodExternalId,
      payment_method_code: paymentMethodCode,
      payment_method_pin: paymentMethodPin,
      amount,
      auto_confirm: false, // always set to false, required to be able to alter order
    });

  getGiftCardBalance = (code: string) =>
    this.http.get<{ id: string; code: string; balance: number; expired: boolean }>(`/api/v1.0/gift-cards/${code}`);

  getExternalGiftCardBalance = (posId: string, cardNumber: string, pin?: string) =>
    this.http.post<ExternalGiftCardBalanceModel>(`/api/v1.0/pos/${posId}/payment-method/balance`, {
      card_number: cardNumber,
      ...(pin ? { pin } : {}),
    });

  deleteOrderPayment = (orderId: string, paymentId: string) =>
    this.http.delete<Order>(`/api/v1.0/orders/${orderId}/payments/${paymentId}`);

  getOrder = (
    orderId: string,
    include?:
      | 'payment_methods'
      | 'payment_methods,refunds'
      | 'payment_methods,refunds,vouchers'
      | 'refunds'
      | 'payment_methods,hold_blockers'
  ) => this.http.get<Order>(`/api/v1.0/orders/${orderId}`, { params: include ? { include } : {} });
  getOrderInvoice = (orderId: string) => this.http.get(`/api/v1.0/orders/${orderId}/invoice`, { responseType: 'blob' });

  setOrderStatus = (orderId: string, status: string) =>
    this.http.put(`/api/v1.0/orders/${orderId}/status?status=${status}`, {});

  setOrderPaid = (orderId: string) => this.http.post(`/api/v1.0/orders/${orderId}/paid`, {});

  setOrdersRefund = (payload: { ids: string[] }) => this.http.post(`/api/v1.0/orders/refund`, payload);

  prepareRefund = (orders: Refund[]) =>
    this.http.post<{ orders: Order[]; refund_mode: RefundMode }>(`/api/v1.0/refunds/prepare`, { orders });

  addRefund = (refund: Refund) => this.http.post<Refund>(`/api/v1.0/refunds`, refund);

  finalizeRefund = (refund: Refund) => this.http.put<Refund>(`/api/v1.0/refunds/refund`, refund);

  // PAUL-3939: discards a refund that was added but never finalized (pending only).
  removeRefund = (refundId: string) => this.http.delete(`/api/v1.0/refunds/${refundId}`);

  processBulkRefund = (refund: Refund) => this.http.post<BulkRefundResponse>(`/api/v1.0/refunds/bulk`, refund);

  retryRefundPayment = (refundId: string, refundPaymentId: string) =>
    this.http.post(`/api/v1.0/refunds/${refundId}/payments/${refundPaymentId}/retry`, {});

  convertRefundPaymentToGiftCard = (refundId: string, refundPaymentId: string, email?: string) =>
    this.http.post(`/api/v1.0/refunds/${refundId}/payments/${refundPaymentId}/convert-to-giftcard`, {
      ...(email ? { email } : {}),
    });

  retryAllFailedRefundPayments = (refundId: string) =>
    this.http.post(`/api/v1.0/refunds/${refundId}/payments/retry-failed-all`, {});

  // PAUL-2852: cancel a single pending refund allocation; its amount returns to the refundable pool.
  cancelRefundPayment = (refundId: string, refundPaymentId: string) =>
    this.http.post(`/api/v1.0/refunds/${refundId}/payments/${refundPaymentId}/cancel`, {});

  // Rebinds orphaned refund payments against PSP-reported refunds and auto-fails aged stuck rows

  resyncRefundStatus = (orderId: string) => this.http.post(`/api/v1.0/orders/refund/status`, { ids: [orderId] });

  resendOrderConfirmation = (payload: { order_id: string; email: string }[]) =>
    this.http.post<ResendOrderConfirmationResult[]>(`/api/v1.0/orders/confirmations/resend`, payload);

  syncPayment = (orderId: string) => this.http.post<Order>(`/api/v1.0/orders/${orderId}/payments/sync`, {});

  exportEmails = (ids: string[]) =>
    this.http.post(`/api/v1.0/orders/export/email-addresses`, { ids }, { responseType: 'text' });

  // Admin

  getOrders = (params?) => this.http.get<DataApiResponseLegacy<Order[]>>('/api/v1.0/orders', { params: params });

  // Vouchers

  getVouchers = (params?) => this.http.get('/api/v1.0/vouchers', { params: params });

  getVoucher = (voucherId) => this.http.get(`/api/v1.0/vouchers/${voucherId}`);

  setVoucherStatus = (voucher: Voucher) =>
    this.http.put(`/api/v1.0/vouchers/${voucher.id}/status`, {
      id: voucher.id,
      status: voucher.status,
    });

  getBarcode = (voucherId) => this.http.get(`/api/v1.0/vouchers/${voucherId}/barcode`, { responseType: 'blob' });

  downloadVoucher = (voucherId: string, purpose = 'download') =>
    this.http.get(`/api/v2.0/vouchers/${voucherId}/pdf`, { responseType: 'blob', params: { purpose } });
  downloadVouchers = (orderId: string, purpose = 'download') =>
    this.http.get(`/api/v2.0/orders/${orderId}/vouchers/pdf`, { responseType: 'blob', params: { purpose } });

  downloadVoucherUrlPath = (urlPath) => this.http.get(urlPath, { responseType: 'blob' });

  resendVoucher = (voucherId) => this.http.post(`/api/v1.0/vouchers/${voucherId}/resend`, {});

  // Comment

  setComment = (cartId, comment: string) => this.http.put(`/api/v1.0/carts/${cartId}/comment`, { comment });

  getVoucherPreview = (body, headers) =>
    this.http.post('/api/v1.0/vouchers/preview', body, { headers, responseType: 'text' });
}
